import z from 'zod';
/**
 * The project's engine movement evidence (DR-3249) — every "this engine went ON" /
 * "this engine came OFF" an `Engine Install Removal Job Cards` document states.
 *
 * ONE envelope per project carrying a flat array, mirroring `bill-of-sale-events-ready`:
 * each job card records its own shop visit, so there is no single winning document to
 * pick, and every row names its own source card instead.
 *
 * `facts` MAY be empty (DR-6342). Without `noMovementFound` that is a RETRACTION: the project
 * stored facts before and now holds no job card. With `noMovementFound: true` the project holds
 * job cards and none of them records a movement. A project with no job card that never stored
 * facts gets no envelope at all.
 *
 * `action` is REQUIRED. It is the whole point of the fact — a movement with no
 * direction says nothing about where the engine went — and the generator only ever
 * emits a fact for a page that explicitly attributed a role, so an absent value would
 * be a producer bug rather than a document that stayed silent.
 *
 * No snapshot hash, and `generatedAt` stays the staleness watermark. Each envelope is the project's
 * COMPLETE set of facts, and the consumer replaces its rows with it. So Brain never sends a partial
 * set: while any job card has a failed extraction page, no envelope is sent (DR-6348). `generation`
 * (DR-5896) names the finalize generation the envelope was built from, so a consumer can tie it to
 * forecast-source's `finalizationGeneration`.
 *
 * CONTRACT: the same `(engineSerial, action)` pair MAY appear several times under different
 * `reconstructedDocumentId`s, and that is EXPECTED. Duplicate uploaded copies of one job card
 * are not collapsed across cards: a job card carries no reliable date, so two cards stating the
 * same movement are indistinguishable from two genuine shop visits, and collapsing them would
 * delete a real one. Consumers must therefore treat each fact as EVIDENCE that a movement is
 * recorded — never as a count of how many times the engine moved.
 */
export const engineMovementEvidenceReadyWebhookSchema = z
    .looseObject({
    type: z.literal('engine-movement-evidence-ready'),
    projectId: z.string(),
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    facts: z.array(z.looseObject({
        action: z.enum(['installation', 'removal']),
        /** Canonical engine serial the fact is about. */
        engineSerial: z.string(),
        /** Representative live document of the job card's reconstructed entity. */
        documentId: z.string(),
        /** Stable id of the reconstructed job card. */
        reconstructedDocumentId: z.string(),
        /** Earliest card page carrying the observation — the evidence deep-link. */
        evidencePageId: z.string(),
    })),
    noMovementFound: z.literal(true).optional(),
})
    .superRefine((payload, context) => {
    if (payload.noMovementFound === true && payload.facts.length > 0) {
        context.addIssue({
            code: 'custom',
            path: ['noMovementFound'],
            message: 'noMovementFound is only sent with empty facts',
        });
    }
});
//# sourceMappingURL=engine-movement-evidence-ready.webhook.js.map